import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { SiteSettingsProvider } from './contexts/SiteSettingsContext';
import { SystemModeProvider } from './contexts/SystemModeContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { WishlistProvider } from './contexts/WishlistContext';
import { NotificationProvider } from './components/Notification/NotificationSystem';
import { FloatingChatProvider } from './contexts/FloatingChatContext';
import { ThemeProvider } from './themes/ThemeProvider';
import ThemedLayout from './themes/ThemedLayout';
import ThemedLoadingScreen from './themes/ThemedLoadingScreen';
import ThemedPage from './themes/ThemedPage';
import SpecialDayEffects from './components/SpecialDayEffects';
import GoogleAnalyticsTracker from './components/Google/GoogleAnalyticsTracker';
import WebPushNotification from './components/WebPushNotification';
import FloatingChatSystem from './components/FloatingChatSystem';
import InstallPWA from './components/InstallPWA';
import { Toaster } from 'react-hot-toast';

import Home from './pages/Home';
import HomeOne from './pages/HomeOne';
import About from './pages/About';
import Services from './pages/Services';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import Payment from './pages/Payment';
import ThankYou from './pages/ThankYou';
import Contact from './pages/Contact';
import Quote from './pages/Quote';
import Appointment from './pages/Appointment';
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';
import Dashboard from './components/Dashboard/Dashboard';
import UserDashboard from './components/Dashboard/UserDashboard';
import AdminDashboard from './components/Dashboard/AdminDashboard';
import UserProfile from './components/Dashboard/UserProfile';
import ProjectDetail from './components/Dashboard/ProjectDetail';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import AdminRoute from './components/Auth/AdminRoute';

import NotFound from './pages/NotFound';
import ComingSoon from './pages/ComingSoon';
import Maintenance from './pages/Maintenance';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsConditions from './pages/TermsConditions';
import RefundPolicy from './pages/RefundPolicy';
import Wishlist from './pages/Wishlist';

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [appReady, setAppReady] = useState(false);

  // Initialize dark mode and app loading
  useEffect(() => {
    const initializeApp = async () => {
      // Initialize dark mode
      const savedTheme = localStorage.getItem('theme');
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      
      if (savedTheme === 'dark' || (!savedTheme && prefersDark)) {
        document.documentElement.classList.add('dark');
      }

      // Simulate app initialization time
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setAppReady(true);
    };

    initializeApp();
  }, []);

  const handleLoadingComplete = () => {
    setIsLoading(false);
  };

  return (
    <>
      {!appReady && (
        <ThemedLoadingScreen 
          isLoading={true} 
          onLoadingComplete={handleLoadingComplete}
        />
      )}
      
      {appReady && (
        <AuthProvider>
          <CartProvider>
            <WishlistProvider>
              <SiteSettingsProvider>
                <ThemeProvider>
                <CurrencyProvider>
                  <NotificationProvider>
                    <Router>
                      <FloatingChatProvider>
                        <SystemModeProvider>
                          <SpecialDayEffects />
                          <GoogleAnalyticsTracker />
                          {/* <WebPushNotification /> */}
                          <FloatingChatSystem />
                          <InstallPWA />
                          <Toaster position="top-right" toastOptions={{ duration: 2500 }} />

                          <Routes>
                            {/* Admin Routes - Full Screen Layout */}
                            <Route path="/admin/*" element={
                              <AdminRoute>
                                <AdminDashboard />
                              </AdminRoute>
                            } />
                            
                            {/* Dashboard Routes - Full Screen Layout */}
                            <Route path="/dashboard" element={
                              <ProtectedRoute>
                                <UserDashboard />
                              </ProtectedRoute>
                            } />
                            <Route path="/dashboard/projects/:projectId" element={
                              <ProtectedRoute>
                                <ProjectDetail />
                              </ProtectedRoute>
                            } />
                            
                            {/* Maintenance and Coming Soon Routes - Simplified Layout */}
                            <Route path="/maintenance" element={<Maintenance />} />
                            <Route path="/coming-soon" element={<ComingSoon />} />
                            
                            {/* All Other Routes - With Header/Footer */}
                            <Route path="*" element={
                              <ThemedLayout>
                                <Routes>
                                  {/* Public Routes */}
                                  <Route path="/" element={<ThemedPage pageKey="home" fallback={Home} />} />
                                  <Route path="/home-one" element={<HomeOne />} />
                                  <Route path="/about" element={<ThemedPage pageKey="about" fallback={About} />} />
                                  <Route path="/services" element={<ThemedPage pageKey="services" fallback={Services} />} />
                                  <Route path="/products" element={<ThemedPage pageKey="products" fallback={Products} />} />
                                  <Route path="/products/:categorySlug" element={<ThemedPage pageKey="products" fallback={Products} />} />
                                  <Route path="/product/:slug" element={<ProductDetail />} />
                                  <Route path="/cart" element={<ThemedPage pageKey="cart" fallback={Cart} />} />
                                  <Route path="/checkout" element={<Checkout />} />
                                  <Route path="/payment" element={<Payment />} />
                                  <Route path="/thank-you" element={<ThankYou />} />
                                  <Route path="/contact" element={<ThemedPage pageKey="contact" fallback={Contact} />} />
                                  <Route path="/quote" element={<ThemedPage pageKey="quote" fallback={Quote} />} />
                                  <Route path="/appointment" element={<Appointment />} />
                                  <Route path="/privacy-policy" element={<ThemedPage pageKey="privacy" fallback={PrivacyPolicy} />} />
                                  <Route path="/terms-conditions" element={<ThemedPage pageKey="terms" fallback={TermsConditions} />} />
                                  <Route path="/refund-policy" element={<ThemedPage pageKey="refund" fallback={RefundPolicy} />} />
                                  <Route path="/wishlist" element={<ThemedPage pageKey="wishlist" fallback={Wishlist} />} />
                                  
                                  {/* Auth Routes */}
                                  <Route path="/login" element={<ThemedPage pageKey="login" fallback={Login} />} />
                                  <Route path="/register" element={<ThemedPage pageKey="register" fallback={Register} />} />
                                  <Route path="/forgot-password" element={<ThemedPage pageKey="forgot" fallback={ForgotPassword} />} />
                                  <Route path="/reset-password" element={<ResetPassword />} />
                                  
                                  {/* Profile Route - With Header/Footer */}
                                  <Route path="/profile" element={
                                    <ProtectedRoute>
                                      <UserProfile />
                                    </ProtectedRoute>
                                  } />
                                  

                                  
                                  <Route path="*" element={<NotFound />} />
                                </Routes>
                              </ThemedLayout>
                            } />
                          </Routes>
                        </SystemModeProvider>
                      </FloatingChatProvider>
                    </Router>
                  </NotificationProvider>
                </CurrencyProvider>
                </ThemeProvider>
              </SiteSettingsProvider>
            </WishlistProvider>
          </CartProvider>
        </AuthProvider>
      )}
    </>
  );
}

export default App;
